/**
 * useChoresQuery — TanStack Query hooks over the chore-pool + my-chores feeds.
 *
 * Plan 02-09 Task 2.  Backend (Plan 02-02 chores.routes) returns:
 *   - GET /api/chores       → PoolCard[] (each row carries `age_gated`, `icon_key`)
 *   - GET /api/chores/board → BoardCard[] (every live status, both kinds)
 *   - GET /api/chores/completed → CompletedCard[] (approved history)
 *
 * Both feeds are server-side filtered for the current session — the route
 * reads `c.get('member')` and computes age_gated against the viewer's
 * birth_date (D-12 server-authoritative).
 *
 * Cache strategy:
 *   - staleTime 30_000  — pool board content changes when WS events land
 *     (useChoresWS invalidates), so a 30s stale window is conservative.
 *   - retry 1           — flaky home network deserves one retry; matches
 *     Phase 1 DashboardQueryProvider default.
 *
 * The hooks intentionally do NOT depend on a session-context wrapper —
 * the route layer authenticates per-request via cookie. If the user is
 * unauthed, fetch returns 401 and the consumer redirects to /login.
 */
import { useQuery } from '@tanstack/react-query';

export type PoolChoreInstance = {
  id: string;
  chore_id: string;
  status:
    | 'available'
    | 'claimed'
    | 'awaiting_approval'
    | 'completed'
    | 'rejected'
    | 'expired';
  claimed_by: string | null;
  /** Joined from members.short_name on the server, attached for read-only display. */
  claimed_by_short_name?: string | null;
  claim_expires_at: string | null;
  proof_photo_url: string | null;
  title_snapshot: string;
  point_value_snapshot: number;
  min_age_snapshot: number;
  tint_snapshot: string | null;
  /** Server-computed flag: true when min_age_snapshot > viewer.age. */
  age_gated: boolean;
  /** Joined from chores.icon_key for the ChoreIcon glyph. */
  icon_key: string | null;
  /** Joined from chores.category for sort/filter; nullable. */
  category: string | null;
  /**
   * Fase 1 assignment. Members eligible to claim this card — empty = open pool.
   * `assignee_short_names` is parallel to `assignee_ids` for display. Optional
   * so older fixtures/feeds without the fields still typecheck; the pool feed
   * always populates them.
   */
  assignee_ids?: string[];
  assignee_short_names?: string[];
  /** Server-computed: true when the viewer may NOT claim a restricted card. */
  assignment_locked?: boolean;
};

async function fetchJson<T>(url: string): Promise<T> {
  const r = await globalThis.fetch(url, { credentials: 'same-origin' });
  if (!r.ok) {
    const err = new Error(`fetch_failed:${r.status}`);
    (err as Error & { status: number }).status = r.status;
    throw err;
  }
  return (await r.json()) as T;
}

/** Pool board feed for the current session. */
export function usePoolBoard() {
  return useQuery<PoolChoreInstance[]>({
    queryKey: ['chores'],
    queryFn: () => fetchJson<PoolChoreInstance[]>('/api/chores'),
    staleTime: 30_000,
    retry: 1,
  });
}

/**
 * One approved chore in the "Ferdig" history tab.
 *
 * `proof_visible` is computed server-side (admin OR the doer) because the
 * proofs route enforces the same gate — rendering a photo the viewer may not
 * fetch would just produce a broken tile for siblings.
 */
export type CompletedChore = PoolChoreInstance & {
  completed_at: string | null;
  approved_at: string | null;
  claimant_short_name: string | null;
  approver_short_name: string | null;
  proof_visible: boolean;
};

/**
 * Whole-family completed-chore history, newest approval first.
 *
 * Shares the pool's 30s stale window; useChoresWS invalidates ['chores'] on
 * approval events, and this key is invalidated alongside it so a Godkjenn on
 * the tablet lands in Ferdig without a manual refresh.
 */
export function useCompletedChores(days = 30, enabled = true) {
  return useQuery<CompletedChore[]>({
    queryKey: ['chores-completed', days],
    queryFn: () => fetchJson<CompletedChore[]>(`/api/chores/completed?days=${days}`),
    staleTime: 30_000,
    retry: 1,
    // History is a deliberate visit, not the pool board's hot path — consumers
    // pass false until their Ferdig tab is actually opened.
    enabled,
  });
}

/**
 * One card on the unified board feed.
 *
 * Superset of PoolChoreInstance: `kind`/`subject` let the board split homework
 * into its own section, and `recurrence` (raw template JSON) feeds the card's
 * repeat hint via describeRecurrence().
 */
export type BoardChoreInstance = PoolChoreInstance & {
  kind: string | null;
  subject: string | null;
  recurrence: string | null;
  due_at: string | null;
};

/**
 * Unified board feed — every live instance (available + claimed +
 * awaiting_approval) across chores AND homework, deadline first.
 *
 * Replaces reconciling three feeds client-side. Because it carries claimed rows
 * for the whole family, the board can derive Mine / Åpne / Alle and per-kid
 * filters in memory, and a card claimed by a sibling is finally visible.
 *
 * Shares the ['chores'] invalidation family so useChoresWS's existing
 * chore-event handling refreshes it without new wiring.
 */
export function useBoard() {
  return useQuery<BoardChoreInstance[]>({
    queryKey: ['chores', 'board'],
    queryFn: () => fetchJson<BoardChoreInstance[]>('/api/chores/board'),
    staleTime: 30_000,
    retry: 1,
  });
}

// useMyChores was removed with the board feed: GET /api/chores/board already
// carries the viewer's held cards, so MyChoresStrip takes them as a prop rather
// than issuing a second request for data its parent is holding. The
// /api/chores/my endpoint remains for other clients.
